// Typed wrappers for the /v1 endpoints (PRD §16). Response types come from
// @maestros/contracts (type-only) or from ./types.ts (mirror of apps/api for API-local
// shapes). No shape normalization: optional fields added later by the API are read with
// explicit fallbacks at the call site.
import type {
  AcceptedOperation,
  Board,
  CreateProjectInput,
  CreateTaskInput,
  ImportPrdInput,
  PrdImportResult,
  Project,
  ProjectSummary,
  Task,
  TaskActionRequest,
  TaskPlacementResult,
} from "@maestros/contracts";
import { ApiRequestError, api, apiUpload } from "./client.ts";
import type {
  CoverageResponse,
  HealthResponse,
  LoginResponse,
  Me,
  MfaEnrollment,
  MfaRegenerationConfirm,
  MfaRegenerationStart,
  OperationRecord,
  PrdListItem,
  ProjectDetailResponse,
  ProjectListResponse,
  SetupResponse,
  TaskDetail,
  TimelineResponse,
} from "./types.ts";

const enc = encodeURIComponent;

// ---------- Health / auth ----------

export async function getHealth(signal?: AbortSignal): Promise<HealthResponse> {
  try {
    return await api<HealthResponse>("/health", signal ? { signal } : {});
  } catch (e) {
    // 503 carries a degraded health body; keep it as data, not as an exception.
    if (e instanceof ApiRequestError && e.status === 503 && e.body && typeof e.body === "object" && "status" in e.body) return e.body as HealthResponse;
    throw e;
  }
}

export const getMe = () => api<Me>("/me");
export const setup = (body: { organization_name: string; display_name: string; email: string; password: string; locale?: string }, setupToken?: string) =>
  api<SetupResponse>("/setup", { method: "POST", body, headers: setupToken ? { "x-setup-token": setupToken } : undefined });
export const login = (body: { email: string; password: string; totp?: string }) => api<LoginResponse>("/auth/login", { method: "POST", body });
export const enrollMfa = () => api<MfaEnrollment>("/auth/mfa/enroll", { method: "POST", body: {} });
export const confirmMfa = (code: string) => api<unknown>("/auth/mfa/confirm", { method: "POST", body: { code } });
export const startMfaRegeneration = (body: { password: string; code: string }) => api<MfaRegenerationStart>("/auth/mfa/regenerate", { method: "POST", body });
export const confirmMfaRegeneration = (code: string) => api<MfaRegenerationConfirm>("/auth/mfa/regenerate/confirm", { method: "POST", body: { code } });
export const logout = () => api<unknown>("/auth/logout", { method: "POST", body: {} });

// ---------- Projects ----------

export interface ProjectListQuery {
  search?: string;
  filter?: "all" | "running" | "needs_attention" | "completed";
  cursor?: string | null;
  limit?: number;
}

/**
 * GET /v1/projects: search, aggregate_state, cursor, limit (max 100) and `counts`.
 * `attention=true` is also sent for "needs_attention" so older API builds still filter.
 */
export const listProjects = (q: ProjectListQuery, signal?: AbortSignal) =>
  api<ProjectListResponse>("/projects", {
    query: {
      search: q.search,
      aggregate_state: q.filter && q.filter !== "all" ? q.filter : undefined,
      attention: q.filter === "needs_attention" ? "true" : undefined,
      cursor: q.cursor ?? undefined,
      limit: Math.min(100, q.limit ?? 50),
    },
    ...(signal ? { signal } : {}),
  });

/** GET /v1/projects/{id}: returns the portfolio summary of the project. */
export async function getProject(id: string): Promise<ProjectSummary> {
  const detail = await api<ProjectDetailResponse>(`/projects/${enc(id)}`);
  return detail.summary;
}

export const createProject = (body: Partial<CreateProjectInput> & { name: string }, idempotencyKey?: string) =>
  api<Project>("/projects", { method: "POST", body, ...(idempotencyKey ? { idempotencyKey } : {}) });

export const updateProject = (id: string, body: Partial<CreateProjectInput> & { expected_version: number; content_language?: string | null }) =>
  api<Project>(`/projects/${enc(id)}`, { method: "PATCH", body });

export const validateProject = (id: string) => api<AcceptedOperation>(`/projects/${enc(id)}/validate`, { method: "POST", body: {} });
export const getOperation = (id: string) => api<OperationRecord>(`/operations/${enc(id)}`);

export type ExecutionAction = "start" | "pause" | "resume" | "drain" | "stop";

/** Engine outcome reported by POST /v1/projects/{id}/execution (state changes are committed first). */
export interface ExecutionEngineReport {
  task_runs: { task_id: string; execution_id: string; started: boolean }[];
  goal_run: { goal_run_id: string; execution_id: string; started: boolean } | null;
  /** `skipped: "execution_closed"`: the execution had already finished (neutral, not an error). */
  signals: { execution_id: string; kind: string; delivered: boolean; skipped?: "execution_closed" }[];
  errors: { target: string; message: string }[];
}

export const changeExecution = (id: string, action: ExecutionAction, expectedVersion: number, reason?: string) =>
  api<Project & { engine?: ExecutionEngineReport }>(`/projects/${enc(id)}/execution`, {
    method: "POST",
    body: { action, expected_version: expectedVersion, ...(reason ? { reason } : {}) },
  });

/** RF-DEL-01..05: what deleting a project removes (shown before the confirmation). */
export interface ProjectDeletionFacts {
  id: string;
  name: string;
  repository_url: string | null;
  task_counts: { total: number; done: number; cancelled: number; in_progress: number; other: number };
  running_attempts: number;
  deletable: boolean;
}
export const getProjectDeletion = (id: string) => api<ProjectDeletionFacts>(`/projects/${enc(id)}/deletion`);
/** Soft-deletes the project; the linked GitHub repository is never deleted. */
export const deleteProject = (id: string) => api<ProjectDeletionFacts & { deleted_at: string }>(`/projects/${enc(id)}`, { method: "DELETE" });

/** Full project detail (readiness included) plus the portfolio summary. */
export const getProjectDetail = (id: string, signal?: AbortSignal) => api<ProjectDetailResponse>(`/projects/${enc(id)}`, signal ? { signal } : {});

// ---------- Tasks / board ----------

/** `placement_result` is absent on replays of creations recorded before placement existed. */
export type CreatedTask = Task & { placement_result?: TaskPlacementResult };

export const createTask = (projectId: string, body: Partial<CreateTaskInput> & { title: string }, idempotencyKey: string) =>
  api<CreatedTask>(`/projects/${enc(projectId)}/tasks`, { method: "POST", body, idempotencyKey });

/** Full task detail (description, acceptance, available_actions, durations). */
export const getTask = (id: string) => api<TaskDetail>(`/tasks/${enc(id)}`);

export const getBoard = (projectId: string, signal?: AbortSignal) => api<Board>(`/projects/${enc(projectId)}/board`, signal ? { signal } : {});

export const taskAction = (taskId: string, body: TaskActionRequest) => api<Task>(`/tasks/${enc(taskId)}/actions`, { method: "POST", body });

export const getTimeline = (taskId: string, cursor?: string | null) =>
  api<TimelineResponse>(`/tasks/${enc(taskId)}/timeline${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`);

// ---------- PRDs ----------

export const importPrd = (projectId: string, body: ImportPrdInput, idempotencyKey: string, onUploadProgress?: (fraction: number) => void) =>
  apiUpload<PrdImportResult>(`/projects/${enc(projectId)}/prds`, body, { idempotencyKey, ...(onUploadProgress ? { onUploadProgress } : {}) });

export const addPrdVersion = (prdId: string, body: ImportPrdInput, idempotencyKey: string, onUploadProgress?: (f: number) => void) =>
  apiUpload<PrdImportResult>(`/prds/${enc(prdId)}/versions`, body, { idempotencyKey, ...(onUploadProgress ? { onUploadProgress } : {}) });

export const listPrds = (projectId: string) => api<{ items: PrdListItem[] }>(`/projects/${enc(projectId)}/prds`);

export const getCoverage = (prdVersionId: string) => api<CoverageResponse>(`/prd-versions/${enc(prdVersionId)}/coverage`);
